import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  BookOpen, TrendingUp, LogIn, LogOut, SlidersHorizontal, AlertTriangle,
  Clock, Layers, Building2, Target, Ban,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { STRATEGIES, LAST_UPDATED } from './ops/strategiesData';

const Section = ({ icon: Icon, title, accent, children }) => (
  <div className="space-y-3" data-testid={`section-${title}`}>
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4" style={{ color: accent }} />
      <h3 className="font-heading font-semibold text-sm uppercase tracking-wide text-foreground/90">{title}</h3>
    </div>
    {children}
  </div>
);

const RuleList = ({ items, accent }) => (
  <div className="space-y-2.5">
    {items.map((it, i) => (
      <div key={i} className="flex gap-3 rounded-md border border-border/60 bg-background/40 p-3">
        <span
          className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold"
          style={{ backgroundColor: `${accent}1a`, color: accent }}
        >
          {i + 1}
        </span>
        <div>
          <p className="text-sm font-medium text-foreground">{it.t}</p>
          <p className="text-sm text-muted-foreground leading-relaxed">{it.d}</p>
        </div>
      </div>
    ))}
  </div>
);

const ParamTable = ({ rows }) => (
  <div className="rounded-md border border-border/60 overflow-hidden">
    <Table>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={i} className="border-border/40">
            <TableCell className="font-medium text-foreground/90 w-[38%]">{r.k}</TableCell>
            <TableCell className="font-mono text-sm text-primary">{r.v}</TableCell>
            <TableCell className="text-xs text-muted-foreground hidden sm:table-cell">{r.n}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

const MetaPill = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2 rounded-md border border-border/60 bg-background/40 px-3 py-2">
    <Icon className="w-4 h-4 text-muted-foreground" />
    <div className="leading-tight">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-sm font-medium text-foreground">{value}</p>
    </div>
  </div>
);

const BotDoc = ({ bot }) => (
  <Card className="border-border/70 bg-card/60 backdrop-blur-sm overflow-hidden" data-testid={`strategy-card-${bot.id}`}>
    <div className="h-1 w-full" style={{ backgroundColor: bot.accent }} />
    <CardHeader>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle className="text-2xl font-heading" style={{ color: bot.accent }}>{bot.name}</CardTitle>
          <CardDescription className="mt-1 font-mono text-xs">{bot.strategyId}</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-green-500/15 text-green-400 border-green-500/30">{bot.status}</Badge>
          {bot.hasTP
            ? <Badge variant="outline" className="border-border/60 text-muted-foreground"><Target className="w-3 h-3 mr-1" />Con TP</Badge>
            : <Badge variant="outline" className="border-amber-500/30 text-amber-400"><Ban className="w-3 h-3 mr-1" />Sin TP (trailing)</Badge>}
        </div>
      </div>
      <p className="mt-3 text-sm text-foreground/80 leading-relaxed">{bot.tagline}</p>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
        <MetaPill icon={Building2} label="Plataforma" value={bot.venue} />
        <MetaPill icon={Layers} label="Mercado" value={bot.market} />
        <MetaPill icon={Clock} label="Temporalidad" value={bot.timeframe} />
        <MetaPill icon={TrendingUp} label="Estado" value={bot.status} />
      </div>
    </CardHeader>

    <CardContent className="space-y-7">
      <Section icon={BookOpen} title="La idea" accent={bot.accent}>
        <p className="text-sm text-muted-foreground leading-relaxed rounded-md border border-border/40 bg-background/30 p-4">
          {bot.idea}
        </p>
      </Section>

      <Section icon={LogIn} title={bot.entryTitle} accent={bot.accent}>
        <RuleList items={bot.entry} accent={bot.accent} />
      </Section>

      <Section icon={LogOut} title={bot.exitTitle} accent={bot.accent}>
        <RuleList items={bot.exit} accent={bot.accent} />
      </Section>

      <Section icon={SlidersHorizontal} title="Parámetros (estado actual)" accent={bot.accent}>
        {bot.paramGroups.map((g, i) => (
          <div key={i} className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{g.group}</p>
            <ParamTable rows={g.rows} />
          </div>
        ))}
      </Section>

      {bot.presets && (
        <Section icon={Layers} title={bot.presets.title} accent={bot.accent}>
          <div className="rounded-md border border-border/60 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border/40">
                  {bot.presets.columns.map((c, i) => (
                    <TableHead key={i} className="text-xs">{c}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {bot.presets.rows.map((row, i) => (
                  <TableRow key={i} className="border-border/40">
                    {row.map((cell, j) => (
                      <TableCell key={j} className={cn('text-sm', j === 0 ? 'font-semibold text-foreground' : 'font-mono text-muted-foreground')}>{cell}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Section>
      )}

      {bot.riskNotes?.length > 0 && (
        <Section icon={AlertTriangle} title="Notas de riesgo" accent={bot.accent}>
          <ul className="space-y-2">
            {bot.riskNotes.map((n, i) => (
              <li key={i} className="flex gap-2 text-sm text-muted-foreground">
                <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ backgroundColor: bot.accent }} />
                <span className="leading-relaxed">{n}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </CardContent>
  </Card>
);

export const Strategies = () => {
  const [active, setActive] = useState(STRATEGIES[0].id);
  const bot = STRATEGIES.find((b) => b.id === active) || STRATEGIES[0];

  return (
    <div className="space-y-6" data-testid="strategies-page">
      <div>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 border border-primary/20">
            <BookOpen className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-heading font-bold bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent">
              Estrategias
            </h1>
            <p className="text-sm text-muted-foreground">
              Documentación viva de cada bot: qué estrategia usa hoy, con qué parámetros y cómo funciona.
            </p>
          </div>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Última actualización: {LAST_UPDATED}</p>
      </div>

      {/* Selector de bots */}
      <div className="flex flex-wrap gap-2" data-testid="strategy-selector">
        {STRATEGIES.map((b) => (
          <button
            key={b.id}
            onClick={() => setActive(b.id)}
            data-testid={`strategy-tab-${b.id}`}
            className={cn(
              'flex items-center gap-2 rounded-md border px-3.5 py-2 text-sm font-medium transition-all duration-200',
              active === b.id
                ? 'bg-card text-foreground shadow-sm'
                : 'border-border/60 text-muted-foreground hover:text-foreground hover:bg-accent'
            )}
            style={active === b.id ? { borderColor: b.accent, boxShadow: `0 0 0 1px ${b.accent}40` } : undefined}
          >
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: b.accent }} />
            {b.name}
          </button>
        ))}
      </div>

      <BotDoc bot={bot} />
    </div>
  );
};

export default Strategies;
